import React, { useState } from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import LanguageIcon from "@mui/icons-material/Language";
import PeopleIcon from "@mui/icons-material/People";
import EmailIcon from "@mui/icons-material/EmailOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { LoadingButton } from "./LoadingButton";
import { useLanguages, LanguageModel } from "../hooks/useLanguages";
import { useEmailTemplates } from "../hooks/emailTemplates/useEmailTemplates";
import { ContactEmailModel, EmailTemplateResponse } from "@attelas/shared";
import { DraftRecipientMode, DEFAULT_DRAFT_RECIPIENT_MODE, hasMultipleContactEmails } from "../utils/contactRecipients";

type Props = {
  onClick?(languageId?: string, recipientMode?: DraftRecipientMode): void;
  loading?: boolean;
  disabled?: boolean;
  contacts?: ContactEmailModel[];
  /** Number of invoices being drafted; used to filter email templates. */
  invoiceCount?: number;
  /** When provided, shows split button with dropdown; clicking "Attach file" calls this with the arrow button element for anchoring the picker. */
  onOpenAttachPickerForDraft?(anchorEl: HTMLElement): void;
  onEmailTemplateClick?(template: EmailTemplateResponse, recipientMode?: DraftRecipientMode): void;
};

const recipientModeLabels: Record<DraftRecipientMode, string> = {
  primary: "Primary contact",
  all: "All contacts",
};

const isTemplateVisibleForInvoiceCount = (templateType: string, invoiceCount: number) => {
  if (templateType === "Standard") return true;
  if (invoiceCount === 1) return templateType === "ReceivableInvoice";
  if (invoiceCount > 1) return templateType === "MultipleReceivableInvoices";
  return false;
};

export function ChaseButton({
  onClick,
  loading = false,
  disabled = false,
  contacts,
  invoiceCount = 0,
  onOpenAttachPickerForDraft,
  onEmailTemplateClick,
}: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const [langMenuAnchor, setLangMenuAnchor] = useState<null | HTMLElement>(null);
  const langMenuOpen = Boolean(langMenuAnchor);

  const [recipientMenuAnchor, setRecipientMenuAnchor] = useState<null | HTMLElement>(null);
  const recipientMenuOpen = Boolean(recipientMenuAnchor);

  const [templateMenuAnchor, setTemplateMenuAnchor] = useState<null | HTMLElement>(null);
  const templateMenuOpen = Boolean(templateMenuAnchor);

  const { languages } = useLanguages();
  const { emailTemplates } = useEmailTemplates();
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageModel | null>(null);
  const langLabel = selectedLanguage ? selectedLanguage.name : "";

  const [selectedRecipientMode, setSelectedRecipientMode] = useState<DraftRecipientMode>(DEFAULT_DRAFT_RECIPIENT_MODE);
  const recipientLabel = recipientModeLabels[selectedRecipientMode];
  const showRecipientOptions = !!contacts && hasMultipleContactEmails(contacts);

  const filteredEmailTemplates =
    emailTemplates?.filter((template) => isTemplateVisibleForInvoiceCount(template.type, invoiceCount)) ?? [];
  const showEmailTemplates = !!onEmailTemplateClick && filteredEmailTemplates.length > 0;

  React.useEffect(() => {
    if (languages && languages.length > 0 && !selectedLanguage) {
      const defaultLang = languages.find((l) => l.name.toLowerCase() === "english") ?? languages[0];
      setSelectedLanguage(defaultLang);
    }
  }, [languages, selectedLanguage]);

  return (
    <>
      <ButtonGroup variant="contained" fullWidth sx={{ "& .MuiButtonGroup-grouped": { minWidth: 0 } }}>
        <LoadingButton
          color="gradient"
          loading={loading}
          loadingPosition="end"
          endIcon={<ArrowForwardIcon />}
          onClick={() => onClick?.(selectedLanguage?.id, selectedRecipientMode)}
          disabled={disabled}
          sx={{ flex: 1, minWidth: 0 }}
        >
          Draft
        </LoadingButton>
        <Button
          aria-controls={menuOpen ? "chase-draft-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={menuOpen ? "true" : undefined}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={disabled}
          sx={{
            minWidth: 32,
            width: 32,
            maxWidth: 32,
            px: 0,
            backgroundColor: "#9A1BF9",
            color: "#FFFFFF",
            "&:hover": {
              backgroundColor: "#8A19E0",
            },
            "& .MuiSvgIcon-root": { fontSize: 18 },
          }}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Menu
        id="chase-draft-menu"
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ list: { "aria-labelledby": "chase-draft-button" } as any }}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {onOpenAttachPickerForDraft && (
          <MenuItem
            onClick={() => {
              const anchor = menuAnchor;
              setMenuAnchor(null);
              if (anchor) onOpenAttachPickerForDraft(anchor);
            }}
          >
            <ListItemIcon>
              <AttachFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Attach file" />
          </MenuItem>
        )}
        {showEmailTemplates && (
          <MenuItem
            onClick={(e) => setTemplateMenuAnchor(e.currentTarget)}
            aria-controls={templateMenuOpen ? "email-template-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={templateMenuOpen ? "true" : undefined}
          >
            <ListItemIcon>
              <EmailIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Email template" />
            <ChevronRightIcon fontSize="small" />
          </MenuItem>
        )}
        {showRecipientOptions && (
          <MenuItem
            onClick={(e) => setRecipientMenuAnchor(e.currentTarget)}
            aria-controls={recipientMenuOpen ? "recipient-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={recipientMenuOpen ? "true" : undefined}
          >
            <ListItemIcon>
              <PeopleIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={recipientLabel} />
            <ChevronRightIcon fontSize="small" />
          </MenuItem>
        )}
        {languages && languages.length > 0 && (
          <MenuItem
            onClick={(e) => setLangMenuAnchor(e.currentTarget)}
            aria-controls={langMenuOpen ? "lang-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={langMenuOpen ? "true" : undefined}
          >
            <ListItemIcon>
              <LanguageIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={langLabel || "Language"} />
            <ChevronRightIcon fontSize="small" />
          </MenuItem>
        )}
      </Menu>
      <Menu
        id="email-template-menu"
        anchorEl={templateMenuAnchor}
        open={templateMenuOpen}
        onClose={() => setTemplateMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {filteredEmailTemplates.map((template) => (
          <MenuItem
            key={template.id}
            onClick={() => {
              setTemplateMenuAnchor(null);
              setMenuAnchor(null);
              onEmailTemplateClick?.(template, selectedRecipientMode);
            }}
          >
            <ListItemText primary={template.name} />
          </MenuItem>
        ))}
      </Menu>
      <Menu
        id="recipient-menu"
        anchorEl={recipientMenuAnchor}
        open={recipientMenuOpen}
        onClose={() => setRecipientMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {(Object.keys(recipientModeLabels) as DraftRecipientMode[]).map((mode) => (
          <MenuItem
            key={mode}
            selected={selectedRecipientMode === mode}
            onClick={() => {
              setSelectedRecipientMode(mode);
              setRecipientMenuAnchor(null);
              setMenuAnchor(null);
            }}
          >
            <ListItemText primary={recipientModeLabels[mode]} />
          </MenuItem>
        ))}
      </Menu>
      <Menu
        id="lang-menu"
        anchorEl={langMenuAnchor}
        open={langMenuOpen}
        onClose={() => setLangMenuAnchor(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        {languages?.map((language) => (
          <MenuItem
            key={language.id}
            selected={selectedLanguage?.id === language.id}
            onClick={() => {
              setSelectedLanguage(language);
              setLangMenuAnchor(null);
              setMenuAnchor(null);
            }}
          >
            <ListItemText primary={language.name} />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
